import React from "react";
import { Printer, CheckCircle } from "@phosphor-icons/react";

/**
 * Printable, client-facing summary of everything shipped in Clariva.
 * Route: /client-summary
 *
 * Print rules:
 *  - Each <section className="print-page"> forces a new page in PDF output.
 *  - "no-print" hides toolbar chrome in the printed output.
 *  - Colors are print-safe (WebKit print-color-adjust: exact).
 */

const CAPABILITIES = [
  {
    title: "1. Contract Creation Studio",
    tag: "Author",
    points: [
      "Modern, Juro-style rich editor with headings, tables, lists, code blocks and variable placeholders",
      "Draft autosave and inline completion progress (title, counterparty, doc-type, content)",
      "Start from a blank canvas, a curated template library, or your own boilerplates",
      "Upload PDF / DOCX / TXT with automatic text extraction (OCR for scanned documents)",
      "One-click AI Contract Formatter that cleans up pasted text into a professional layout",
    ],
  },
  {
    title: "2. AI Contract Intelligence",
    tag: "Analyze",
    points: [
      "Clause-by-clause analysis with plain-language rewrites and risk scoring",
      "For / against clause breakdown so both parties understand the impact",
      "Loophole detection and negotiation prompts powered by Claude Sonnet 4.5",
      "Bank loan summarizer — extracts APR, EMI, tenure, covenants, prepayment rules and risk flags",
      "8-domain Business Impact Report (finance, legal, operations, reputation, compliance, HR, tech, strategy) with radial gauges",
      "Natural-language Q&A over the full contract repository (Ask Clariva / Cmd+K palette)",
      "Audio briefings (studio-quality TTS narration for signatories)",
    ],
  },
  {
    title: "3. Approval Workflow Studio",
    tag: "Route",
    points: [
      "Drag-and-drop visual designer with 10 activity types (approvals, decisions, AI analyze, e-signature, forms, delays, notifications, auto actions)",
      "Fork / join branches with conditional routing (amount thresholds, category rules, custom expressions)",
      "Version control: publish v1 → v2 → v3, archive old flows without losing history",
      "Live workflow runner with pulsing current stage, traversed-path highlights and full audit timeline",
      "Approve, reject, cancel, or reroute with mandatory notes for compliance",
      "Doc-Type → Workflow mapping — every contract category auto-routes to the right approval path",
    ],
  },
  {
    title: "4. Transparent Signatory Experience",
    tag: "Sign",
    points: [
      "Dedicated signing portal that explains every clause in plain English",
      "OTP-verified e-signature with full audit trail (who, what, when, from where)",
      "Public briefing links with QR codes — share a contract summary without a login",
      "Clariva Share Inbox — external signatories see every contract awaiting them across all customers in one branded inbox",
      "Magic-link authentication for signatories (no passwords, no accounts)",
    ],
  },
  {
    title: "5. Repository, Search & Monitoring",
    tag: "Manage",
    points: [
      "Central repository with filterable grid, status chips and risk indicators",
      "Semantic search (AI-powered) with contextual snippets from clause bodies",
      "Access-controlled per-contract (owners, signers, viewers) — signatories only see what's shared with them",
      "Obligation tracking with SLA breach alerts and inline status updates",
      "Obligation detail drawer with linked contract and AI-matched clause excerpt",
      "Cross-contract ripple view — see related contracts by counterparty or tags",
    ],
  },
  {
    title: "6. Analytics & Business Intelligence",
    tag: "Insight",
    points: [
      "Executive dashboard with drag-and-drop widgets, KPIs and multi-series charts",
      "Top counterparties, contract velocity, risk distribution, expiry heatmap",
      "8-domain business impact scoring per contract and portfolio-wide",
      "Exportable reports for stakeholders and board reviews",
    ],
  },
];

const ENTERPRISE = [
  {
    title: "Multi-Tenant Architecture",
    points: [
      "Complete data isolation per customer (tenant)",
      "Global Admin portal for cross-tenant support, tenant provisioning wizard, and impersonation with full audit logging",
      "Self-service tenant creation with slug validation and rollback safety",
    ],
  },
  {
    title: "Roles & Permissions (RBAC)",
    points: [
      "5 built-in roles: Admin, Contract Manager, Manager, Signatory, Viewer",
      "Visual role-vs-module matrix (16 modules) — admins configure exactly what each role can see",
      "Guardrails prevent privilege escalation, self-demotion, or accidental admin lockout",
      "Per-contract Access Control Lists (ACL) for granular sharing beyond RBAC",
    ],
  },
  {
    title: "Authentication & SSO",
    points: [
      "Email / password with bcrypt hashing and 24-hour JWT sessions",
      "Google OAuth via Emergent-managed sign-in",
      "SAML 2.0, OIDC and Active Directory / LDAP configuration panels",
      "Live LDAP bind support (tested against public directories)",
      "Auto-detection: login form flips to \"Sign in with AD/LDAP\" for enterprise tenants",
      "Force password reset on first login for admin-provisioned accounts",
    ],
  },
  {
    title: "Compliance & Location",
    points: [
      "13 frameworks supported: GDPR, HIPAA, CCPA, PDPA-SG, PDPL-UAE, DIFC-DPL, SOX, ISO-27001, SOC 2, PCI DSS, FERPA, eIDAS, NHS-DSP",
      "7 regions × 11 industries with recommended-framework filtering",
      "Compliance-aware boilerplate and template recommendations",
    ],
  },
  {
    title: "Global Reach",
    points: [
      "8 languages: English, Arabic, French, Spanish, German, Japanese, Chinese, Korean",
      "Full RTL (right-to-left) layout support for Arabic",
      "Multi-currency (USD, EUR, GBP, AED, SAR, SGD, JPY and more) with live conversion display",
      "Per-user preferences: language, timezone, theme, notification channels",
    ],
  },
];

const BRANDING = [
  {
    title: "Tenant Branding",
    points: [
      "Upload your logo, set primary and accent colours",
      "Custom terms & conditions, footer text, company address, contact email",
      "Branding applied to sidebar, top bar, PDF exports and all outbound emails",
    ],
  },
  {
    title: "PDF Document Designer",
    points: [
      "Fully customizable letterhead — header, footer and watermark with left / center / right rich-text zones",
      "Tokens: page numbers, total pages, contract title, counterparty, date, logo",
      "Watermarks (DRAFT, CONFIDENTIAL, custom text) with adjustable opacity, rotation and font size",
      "Live watermark preview while designing",
      "Named preset library — save different letterheads for NDAs, MSAs, HR contracts and route them automatically per document type",
    ],
  },
  {
    title: "Boilerplate Library — 15+ Templates",
    points: [
      "SaaS, MSA, Consulting, Employment, Mutual NDA, GDPR DPA, HIPAA BAA",
      "Music Distribution, Clinical Services, Hotel Management, Influencer, Government Procurement",
      "Real Estate: Off-Plan Sale, Cash Sale, Residential & Commercial Lease, Property Management",
      "Banking: Personal Loan, Home / Mortgage Loan, Business / Working Capital",
      "Insurance: Proposal, Policy",
      "Patient Surgery Consent (hospital-grade informed consent + billing)",
      "Custom boilerplates — tenants can create, edit and share their own",
    ],
  },
  {
    title: "Industry Starter Packs",
    points: [
      "One-click provisioning: boilerplates + workflows + PDF presets + doc-type mappings",
      "4 built-in packs: SaaS & Tech, Healthcare, Financial Services, Professional Services",
      "Import your own pack (JSON / YAML) or export your current setup to share across tenants",
      "Idempotent re-imports safely heal orphan configurations",
    ],
  },
];

const INTEGRATIONS = [
  {
    title: "ERP / CRM Integration Bus",
    points: [
      "12 connectors: Salesforce, SAP, HubSpot, NetSuite, DocuSign, Microsoft Dynamics, Oracle, Workday, Slack, Teams, Jira, ServiceNow",
      "Card-based catalogue with connect / disconnect workflows",
    ],
  },
  {
    title: "Communication Channels",
    points: [
      "Email delivery via Resend with tenant-branded HTML templates",
      "SMS and WhatsApp via Twilio (tenant-scoped API keys)",
      "Public shareable links with tokenised access",
    ],
  },
  {
    title: "Modern App Experience",
    points: [
      "Progressive Web App (PWA) — installable on desktop and mobile home screens",
      "Offline caching via service worker",
      "Web Push notifications with VAPID keys",
      "Fully responsive: dedicated mobile navigation drawer + bottom tab bar",
      "Landing page includes a QR code for instant mobile install",
    ],
  },
  {
    title: "Design & Theming",
    points: [
      "4 curated theme packs: Midnight Slate (dark), Clariva Ivory (light), Emerald Executive, Cerulean Trust",
      "Light and dark mode per theme",
      "Glassmorphic UI with layered translucency, grain textures and micro-animations",
      "Drag-and-drop dashboard widgets, personalized per user",
    ],
  },
];

const SUPPORT = [
  "In-app \"Raise an issue\" widget — floating support button on every authenticated page",
  "Global support inbox for Clariva staff (cross-tenant ticket triage)",
  "Live impersonation with amber banner + one-click end-session (audit-logged)",
  "Subscription plans: Free, Starter, Pro, Enterprise — with live usage meters",
  "Notifications center with in-app bell and multi-channel delivery",
];

const HIGHLIGHTS = [
  { k: "9", v: "Core CLM Modules" },
  { k: "15+", v: "Contract Templates" },
  { k: "11", v: "Industries Served" },
  { k: "8", v: "Languages" },
  { k: "13", v: "Compliance Frameworks" },
  { k: "16", v: "RBAC-Governed Modules" },
];

const Section = ({ children, id }) => (
  <section id={id} className="print-page">
    {children}
  </section>
);

const CapabilityBlock = ({ item }) => (
  <div className="cap-block">
    <div className="cap-head">
      <span className="cap-tag">{item.tag}</span>
      <h3 className="cap-title">{item.title}</h3>
    </div>
    <ul className="cap-list">
      {item.points.map((p) => (
        <li key={p}>
          <CheckCircle size={16} weight="fill" className="cap-check" />
          <span>{p}</span>
        </li>
      ))}
    </ul>
  </div>
);

const GroupBlock = ({ item }) => (
  <div className="grp-block">
    <h3 className="grp-title">{item.title}</h3>
    <ul className="grp-list">
      {item.points.map((p) => (
        <li key={p}>{p}</li>
      ))}
    </ul>
  </div>
);

export default function ClientSummary() {
  return (
    <div className="cs-root">
      <style>{`
        /* Screen styles */
        .cs-root {
          background: #f7f6f2;
          min-height: 100vh;
          color: #1a1a1a;
          font-family: 'Manrope', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          padding: 24px 16px 64px;
        }
        .cs-doc {
          max-width: 880px;
          margin: 0 auto;
          background: #fff;
          border: 1px solid #e5e2d9;
          border-radius: 6px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.06);
        }
        .cs-toolbar {
          position: sticky;
          top: 0;
          z-index: 10;
          background: #1a1a1a;
          color: #fff;
          padding: 12px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-radius: 6px 6px 0 0;
        }
        .cs-toolbar-title {
          font-family: 'Outfit', sans-serif;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.02em;
        }
        .cs-print-btn {
          background: #d4a86a;
          color: #1a1a1a;
          border: none;
          padding: 8px 16px;
          font-weight: 600;
          font-size: 13px;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .cs-print-btn:hover { background: #c99954; }

        /* Page structure — each .print-page becomes a fresh PDF page */
        .print-page {
          padding: 48px 56px;
          border-bottom: 1px dashed #e5e2d9;
        }
        .print-page:last-child { border-bottom: none; }

        h1.cs-h1 {
          font-family: 'Outfit', sans-serif;
          font-size: 40px;
          font-weight: 700;
          letter-spacing: -0.02em;
          margin: 0 0 8px 0;
          color: #1a1a1a;
        }
        .cs-lede {
          font-size: 15px;
          line-height: 1.6;
          color: #555;
          margin: 8px 0 32px 0;
        }
        .cs-subhead {
          font-family: 'Outfit', sans-serif;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          color: #d4a86a;
          font-weight: 600;
          margin-bottom: 8px;
        }
        h2.cs-h2 {
          font-family: 'Outfit', sans-serif;
          font-size: 26px;
          font-weight: 700;
          letter-spacing: -0.01em;
          margin: 0 0 24px 0;
          padding-bottom: 12px;
          border-bottom: 2px solid #1a1a1a;
        }
        .cs-hero-meta {
          display: flex;
          gap: 24px;
          padding: 20px 0;
          border-top: 1px solid #e5e2d9;
          border-bottom: 1px solid #e5e2d9;
          margin: 32px 0;
          font-size: 13px;
          color: #555;
        }
        .cs-hero-meta strong { color: #1a1a1a; display: block; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 4px; }

        .cs-highlights {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin: 24px 0;
        }
        .cs-highlight {
          background: #faf8f2;
          border: 1px solid #e5e2d9;
          padding: 20px 16px;
          border-radius: 4px;
          text-align: center;
        }
        .cs-highlight-k {
          font-family: 'Outfit', sans-serif;
          font-size: 34px;
          font-weight: 700;
          color: #1a1a1a;
          line-height: 1;
        }
        .cs-highlight-v {
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #666;
          margin-top: 8px;
        }

        .cap-block { margin-bottom: 28px; }
        .cap-block:last-child { margin-bottom: 0; }
        .cap-head { display: flex; align-items: baseline; gap: 12px; margin-bottom: 10px; }
        .cap-tag {
          font-family: 'Outfit', sans-serif;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          background: #1a1a1a;
          color: #d4a86a;
          padding: 4px 8px;
          border-radius: 2px;
          font-weight: 600;
        }
        .cap-title {
          font-family: 'Outfit', sans-serif;
          font-size: 18px;
          font-weight: 700;
          margin: 0;
          color: #1a1a1a;
        }
        .cap-list, .grp-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .cap-list li {
          display: flex;
          gap: 10px;
          align-items: flex-start;
          font-size: 13.5px;
          line-height: 1.55;
          padding: 5px 0;
          color: #333;
        }
        .cap-check {
          color: #2d8659;
          flex-shrink: 0;
          margin-top: 3px;
        }

        .grp-block { margin-bottom: 24px; break-inside: avoid; }
        .grp-title {
          font-family: 'Outfit', sans-serif;
          font-size: 16px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 8px 0;
          padding-left: 12px;
          border-left: 3px solid #d4a86a;
        }
        .grp-list li {
          font-size: 13.5px;
          line-height: 1.55;
          padding: 3px 0 3px 24px;
          color: #333;
          position: relative;
        }
        .grp-list li::before {
          content: "•";
          position: absolute;
          left: 12px;
          color: #d4a86a;
          font-weight: 700;
        }

        .cs-closing {
          background: #1a1a1a;
          color: #f7f6f2;
          padding: 40px;
          margin: 32px -56px -48px;
          border-radius: 0 0 6px 6px;
        }
        .cs-closing h2 {
          color: #d4a86a;
          font-family: 'Outfit', sans-serif;
          font-size: 24px;
          margin: 0 0 12px 0;
        }
        .cs-closing p {
          font-size: 14px;
          line-height: 1.7;
          color: #d4c6a8;
          margin: 0;
        }

        /* Print styles — this is the important bit for PDF export */
        @media print {
          @page {
            size: A4;
            margin: 16mm 14mm;
          }
          .cs-root { background: #fff; padding: 0; }
          .cs-doc { border: none; box-shadow: none; max-width: 100%; }
          .no-print { display: none !important; }
          .print-page {
            padding: 0;
            border-bottom: none;
            page-break-after: always;
            break-after: page;
          }
          .print-page:last-child {
            page-break-after: auto;
            break-after: auto;
          }
          .cap-block, .grp-block { break-inside: avoid; }
          h1.cs-h1, h2.cs-h2, h3.cap-title, h3.grp-title {
            break-after: avoid;
          }
          .cs-closing {
            margin: 0;
            border-radius: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .cap-tag, .cs-highlight, .cs-toolbar {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>

      <div className="cs-doc">
        <div className="cs-toolbar no-print" data-testid="client-summary-toolbar">
          <div className="cs-toolbar-title">Clariva — Client Summary (Print-Ready)</div>
          <button
            className="cs-print-btn"
            onClick={() => window.print()}
            data-testid="print-summary-btn"
          >
            <Printer size={16} weight="bold" />
            Print / Save as PDF
          </button>
        </div>

        {/* PAGE 1 — Cover + Highlights */}
        <Section id="cover">
          <div className="cs-subhead">Product Overview</div>
          <h1 className="cs-h1">Clariva</h1>
          <div className="cs-lede">
            Transparent AI Contract Intelligence — the world's first Contract Lifecycle
            Management platform that serves both sides of a contract (the drafter and the
            signatory) with equal AI intelligence, built for business teams and not just lawyers.
          </div>

          <div className="cs-hero-meta">
            <div>
              <strong>Prepared for</strong>
              Client review
            </div>
            <div>
              <strong>Platform</strong>
              Web · Mobile · PWA
            </div>
            <div>
              <strong>Deployment</strong>
              Multi-tenant SaaS
            </div>
            <div>
              <strong>Availability</strong>
              8 languages · 11 industries
            </div>
          </div>

          <div className="cs-subhead">At a Glance</div>
          <div className="cs-highlights">
            {HIGHLIGHTS.map((h) => (
              <div className="cs-highlight" key={h.v}>
                <div className="cs-highlight-k">{h.k}</div>
                <div className="cs-highlight-v">{h.v}</div>
              </div>
            ))}
          </div>

          <div className="cs-subhead" style={{ marginTop: 32 }}>What's Inside</div>
          <ul className="grp-list" style={{ fontSize: 14 }}>
            <li>Core CLM Capabilities — Create, Analyze, Route, Sign, Manage, Report</li>
            <li>Enterprise Foundations — Multi-tenancy, RBAC, SSO, Compliance, i18n</li>
            <li>Branding & Document Design — Letterheads, watermarks, presets, templates</li>
            <li>Integrations & App Experience — ERP/CRM bus, notifications, PWA, mobile</li>
            <li>Support & Commercials — Ticketing, subscriptions, impersonation</li>
          </ul>
        </Section>

        {/* PAGE 2 — Core Capabilities Part 1 */}
        <Section id="capabilities-1">
          <div className="cs-subhead">Section One</div>
          <h2 className="cs-h2">Core CLM Capabilities</h2>
          {CAPABILITIES.slice(0, 3).map((c) => (
            <CapabilityBlock key={c.title} item={c} />
          ))}
        </Section>

        {/* PAGE 3 — Core Capabilities Part 2 */}
        <Section id="capabilities-2">
          <div className="cs-subhead">Section One · continued</div>
          <h2 className="cs-h2">Core CLM Capabilities</h2>
          {CAPABILITIES.slice(3).map((c) => (
            <CapabilityBlock key={c.title} item={c} />
          ))}
        </Section>

        {/* PAGE 4 — Enterprise Foundations */}
        <Section id="enterprise">
          <div className="cs-subhead">Section Two</div>
          <h2 className="cs-h2">Enterprise Foundations</h2>
          {ENTERPRISE.map((c) => (
            <GroupBlock key={c.title} item={c} />
          ))}
        </Section>

        {/* PAGE 5 — Branding & Documents */}
        <Section id="branding">
          <div className="cs-subhead">Section Three</div>
          <h2 className="cs-h2">Branding & Document Design</h2>
          {BRANDING.map((c) => (
            <GroupBlock key={c.title} item={c} />
          ))}
        </Section>

        {/* PAGE 6 — Integrations & Experience */}
        <Section id="integrations">
          <div className="cs-subhead">Section Four</div>
          <h2 className="cs-h2">Integrations & App Experience</h2>
          {INTEGRATIONS.map((c) => (
            <GroupBlock key={c.title} item={c} />
          ))}
        </Section>

        {/* PAGE 7 — Support & Closing */}
        <Section id="support">
          <div className="cs-subhead">Section Five</div>
          <h2 className="cs-h2">Support, Subscriptions & Operations</h2>
          <ul className="grp-list" style={{ marginBottom: 32 }}>
            {SUPPORT.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>

          <div className="cs-closing">
            <h2>Ready when you are.</h2>
            <p>
              Clariva is production-ready across contract authoring, AI intelligence,
              approvals, signing, monitoring and reporting — with the enterprise
              guardrails, branding controls and integrations expected of a modern CLM.
              This document summarizes the currently shipped functionality; a live
              product walkthrough or tailored demonstration can be arranged on request.
            </p>
          </div>
        </Section>
      </div>
    </div>
  );
}
